import React, { useMemo } from "react";
import DOMPurify from "dompurify";

const CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "hr", "strong", "em", "u", "s",
    "h2", "h3", "h4",
    "ul", "ol", "li",
    "a", "blockquote", "code", "pre",
    "img", "span",
  ],
  ALLOWED_ATTR: [
    "href", "title", "target", "rel",
    "src", "alt", "width", "height", "loading",
  ],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
};

/**
 * Public-side sanitized HTML renderer.
 *
 * Wraps the sanitized HTML in a `prose prose-navy max-w-none` container.
 * Always returns a stable container element so React's reconciler does not
 * transition between `null` and a `dangerouslySetInnerHTML` element across
 * re-renders.
 *
 * NOTE: When you need to render a *list* of many sanitized blocks (e.g. the
 * /faq accordion), prefer a hand-written paragraph splitter instead of this
 * component — rendering 10+ RichHtml instances inside a single list together
 * with react-helmet-async has been observed to crash the React 18 reconciler
 * (`commitDeletionEffectsOnFiber` -> removeChild). For single-instance use
 * (service.description, division.overview, project.body_html, post.body,
 * page.body_html) this component is safe.
 */
export default function RichHtml({ html, className = "", testId }) {
  const safe = useMemo(() => {
    if (!html) return "";
    const cleaned = DOMPurify.sanitize(html, CONFIG);
    if (cleaned && !/<(p|h[2-4]|ul|ol|blockquote|pre|hr)[\s>]/i.test(cleaned)) {
      return `<p>${cleaned}</p>`;
    }
    return cleaned;
  }, [html]);

  return (
    <div
      className={`prose prose-navy max-w-none ${className}`}
      data-testid={testId}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

/** Returns true if HTML contains any non-whitespace text or tags. */
export function isHtmlEmpty(html) {
  if (!html) return true;
  const stripped = html.replace(/<[^>]+>/g, "").trim();
  return stripped.length === 0;
}
